import { Injectable } from '@angular/core';
import { Feature, FeatureCollection, GeoJsonProperties, LineString, Point, Polygon } from 'geojson';
import { GeoJSONSource } from 'maplibre-gl';

import { RectangleBounds } from '@connect-services/client/services/map/bbox-shaping/bbox-geometry.utils';
import { MapBaseService } from '@connect-services/client/services/map-base.service';

type FeatureKind = 'corner' | 'edge' | 'rectangle';

export type CornerKey = 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight';
export type EdgeKey = 'bottom' | 'left' | 'right' | 'top';

export const SHAPING_LAYER_IDS = {
    cornersLayer: 'rectangle-corners-layer',
    edgesHitLayer: 'rectangle-edges-hit-layer',
    fillHitLayer: 'rectangle-fill-hit-layer',
    fillLayer: 'rectangle-fill-layer',
    lineLayer: 'rectangle-line-layer',
    source: 'rectangle-shape-source',
};

const SHAPE_COLOR = '#f28c96';

@Injectable({
    providedIn: 'root',
})
export class MapBboxRenderService extends MapBaseService {
    public cleanMap(): void {
        if (!this.map) {
            return;
        }

        this.map.getCanvas().style.cursor = '';

        const { source, ...layers } = SHAPING_LAYER_IDS;

        Object.values(layers).forEach((layerId) => {
            if (this.map!.getLayer(layerId)) {
                this.map!.removeLayer(layerId);
            }
        });

        if (this.map.getSource(source)) {
            this.map.removeSource(source);
        }
    }

    public clearSource(): void {
        if (this.map?.getSource(SHAPING_LAYER_IDS.source)) {
            (this.map.getSource(SHAPING_LAYER_IDS.source) as GeoJSONSource).setData({
                features: [],
                type: 'FeatureCollection',
            });
        }
    }

    public initShapeSourceAndLayers(): void {
        if (!this.map) {
            return;
        }

        this.addSourceIfMissing();
        this.addFillLayer();
        this.addFillHitLayer();
        this.addLineLayer();
        this.addEdgesHitLayer();
        this.addCornersLayer();
    }

    public renderRectangleFromBounds(bounds: RectangleBounds): void {
        if (!this.map) {
            return;
        }

        const { minLng, maxLng, minLat, maxLat } = bounds;
        const bottomLeft: [number, number] = [minLng, minLat];
        const bottomRight: [number, number] = [maxLng, minLat];
        const topLeft: [number, number] = [minLng, maxLat];
        const topRight: [number, number] = [maxLng, maxLat];

        const featureCollection: FeatureCollection<Polygon | Point | LineString, GeoJsonProperties> = {
            features: [
                this.createRectangleFeature(bottomLeft, bottomRight, topLeft, topRight),
                this.createCornerFeature(topLeft, 'topLeft'),
                this.createCornerFeature(topRight, 'topRight'),
                this.createCornerFeature(bottomRight, 'bottomRight'),
                this.createCornerFeature(bottomLeft, 'bottomLeft'),
                this.createEdgeFeature([topLeft, topRight], 'top'),
                this.createEdgeFeature([topRight, bottomRight], 'right'),
                this.createEdgeFeature([bottomRight, bottomLeft], 'bottom'),
                this.createEdgeFeature([bottomLeft, topLeft], 'left'),
            ],
            type: 'FeatureCollection',
        };

        (this.map.getSource(SHAPING_LAYER_IDS.source) as GeoJSONSource).setData(featureCollection);
    }

    private addCornersLayer(): void {
        this.addLayerIfMissing({
            filter: ['==', ['get', 'kind'], 'corner'],
            id: SHAPING_LAYER_IDS.cornersLayer,
            paint: { 'circle-color': SHAPE_COLOR, 'circle-radius': 4 },
            source: SHAPING_LAYER_IDS.source,
            type: 'circle',
        });
    }

    private addEdgesHitLayer(): void {
        this.addLayerIfMissing({
            filter: ['==', ['get', 'kind'], 'edge'],
            id: SHAPING_LAYER_IDS.edgesHitLayer,
            paint: { 'line-opacity': 0, 'line-width': 24 },
            source: SHAPING_LAYER_IDS.source,
            type: 'line',
        });
    }

    private addFillHitLayer(): void {
        this.addLayerIfMissing({
            filter: ['==', ['get', 'kind'], 'rectangle'],
            id: SHAPING_LAYER_IDS.fillHitLayer,
            paint: { 'fill-opacity': 0 },
            source: SHAPING_LAYER_IDS.source,
            type: 'fill',
        });
    }

    private addFillLayer(): void {
        this.addLayerIfMissing({
            filter: ['==', ['get', 'kind'], 'rectangle'],
            id: SHAPING_LAYER_IDS.fillLayer,
            paint: { 'fill-color': SHAPE_COLOR, 'fill-opacity': 0.2 },
            source: SHAPING_LAYER_IDS.source,
            type: 'fill',
        });
    }

    private addLayerIfMissing(config: Parameters<NonNullable<typeof this.map>['addLayer']>[0]): void {
        if (this.map!.getLayer(config.id)) {
            return;
        }
        this.map!.addLayer(config);
    }

    private addLineLayer(): void {
        this.addLayerIfMissing({
            filter: ['==', ['get', 'kind'], 'rectangle'],
            id: SHAPING_LAYER_IDS.lineLayer,
            paint: { 'line-color': SHAPE_COLOR, 'line-width': 2 },
            source: SHAPING_LAYER_IDS.source,
            type: 'line',
        });
    }

    private addSourceIfMissing(): void {
        if (this.map!.getSource(SHAPING_LAYER_IDS.source)) {
            return;
        }

        this.map!.addSource(SHAPING_LAYER_IDS.source, {
            data: { features: [], type: 'FeatureCollection' },
            type: 'geojson',
        });
    }

    private createCornerFeature(coordinates: [number, number], key: CornerKey): Feature<Point, GeoJsonProperties> {
        return {
            geometry: { coordinates, type: 'Point' },
            properties: { key, kind: 'corner' satisfies FeatureKind },
            type: 'Feature',
        };
    }

    private createEdgeFeature(
        coordinates: [[number, number], [number, number]],
        key: EdgeKey,
    ): Feature<LineString, GeoJsonProperties> {
        return {
            geometry: { coordinates, type: 'LineString' },
            properties: { key, kind: 'edge' satisfies FeatureKind },
            type: 'Feature',
        };
    }

    private createRectangleFeature(
        bottomLeft: [number, number],
        bottomRight: [number, number],
        topLeft: [number, number],
        topRight: [number, number],
    ): Feature<Polygon, GeoJsonProperties> {
        return {
            geometry: {
                coordinates: [[topLeft, topRight, bottomRight, bottomLeft, topLeft]],
                type: 'Polygon',
            },
            properties: { kind: 'rectangle' satisfies FeatureKind },
            type: 'Feature',
        };
    }
}
